import { useState } from "react";
import { toast } from "sonner";
import { Key } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";

const HINTS = {
  GEMINI_API_KEY: "Transcription. Blank = Emergent universal key. Required for Gemini 3.5 Transcribe.",
  ANTHROPIC_API_KEY: "Reasoning + flag fallback. Blank = Emergent universal key. Own key enables direct prompt caching.",
  FIREWORKS_API_KEY: "Flag check + alternate reasoning. Blank = flag check falls back to Anthropic.",
  ELEVENLABS_API_KEY: "Read aloud voice, and the Scribe transcription engine (key needs Speech to Text permission). Optional.",
  SARVAM_API_KEY: "Transcription fallback when Gemini fails a chunk, and the Sarvam read-aloud voice. Optional.",
  EMERGENT_LLM_KEY: "Universal key used when a provider key above is blank.",
  OBSERVER_PASSWORD: "The admin password: opens the Admin area at /admin/login. Changing it signs the admin out everywhere.",
};

const KeyRow = ({ k, onSaved }) => {
  const [v, setV] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async (value) => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/keys", { name: k.name, value });
      onSaved(data.keys);
      setV("");
      toast.success(value ? `${k.name} saved` : `${k.name} override cleared`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="grid sm:grid-cols-[240px_1fr_auto] gap-3 items-center px-5 py-3.5 border-b border-[var(--o-border-soft)] last:border-b-0" data-testid={`key-row-${k.name}`}>
      <div>
        <div className="font-mono text-xs">{k.name}</div>
        <div className="text-[11px] text-[var(--o-muted3)] mt-0.5">{HINTS[k.name]}</div>
      </div>
      <div className="flex items-center gap-3">
        <span className="font-mono text-xs text-[var(--o-muted)] w-32 shrink-0" data-testid={`key-masked-${k.name}`}>{k.set ? k.masked : "not set"}</span>
        <span className={`font-mono text-[10px] uppercase px-1.5 py-0.5 border ${k.source === "admin" ? "border-[#38BDF8]/40 text-[#38BDF8]" : k.source === "env" ? "border-[var(--o-border-strong)] text-[var(--o-muted)]" : "border-[var(--o-border)] text-[var(--o-muted3)]"}`} data-testid={`key-source-${k.name}`}>{k.source}</span>
        <input type="password" value={v} onChange={(e) => setV(e.target.value)} placeholder="Paste new value" autoComplete="new-password" className="flex-1 min-w-0 bg-[var(--o-bg)] border border-[var(--o-border)] px-3 py-2 font-mono text-xs outline-none focus:border-[var(--o-fg)] transition-colors" data-testid={`key-input-${k.name}`} />
      </div>
      <div className="flex gap-2">
        <button disabled={!v || busy} onClick={() => save(v)} className="font-mono text-xs bg-[var(--o-fg)] text-[var(--o-bg)] px-3 py-2 disabled:opacity-30 transition-opacity" data-testid={`key-save-${k.name}`}>Save</button>
        <button disabled={k.source !== "admin" || busy} onClick={() => save("")} className="font-mono text-xs border border-[var(--o-border)] px-3 py-2 hover:bg-[var(--o-surface)] disabled:opacity-30 transition-colors" data-testid={`key-clear-${k.name}`}>Clear</button>
      </div>
    </div>
  );
};

export const KeysPanel = ({ keys, onSaved }) => (
  <div className="border border-[var(--o-border)]" data-testid="keys-panel">
    <div className="px-5 py-4 border-b border-[var(--o-border)] flex items-center gap-3">
      <Key size={18} />
      <div>
        <h2 className="font-display text-base md:text-lg font-bold">API keys</h2>
        <p className="text-xs text-[var(--o-muted2)] mt-1">Values are never shown back. Admin overrides are stored locally in backend/data (gitignored) and take precedence over .env.</p>
      </div>
    </div>
    {keys.map((k) => <KeyRow key={k.name} k={k} onSaved={onSaved} />)}
  </div>
);
